import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { Activity, Zap, Target, TrendingUp, FileText, Users, Upload, Loader2, Scale, Heart, ArrowRight, Flame } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Landing() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [testFile, setTestFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [testWeight, setTestWeight] = useState("75");

  const handleTestUpload = async () => {
    if (!testFile) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", testFile);
      
      const weight = parseFloat(testWeight) || 75;
      const response = await fetch(`${API}/analysis/preview?weight=${weight}`, {
        method: "POST",
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        // Store preview data in sessionStorage and navigate to preview page
        sessionStorage.setItem("previewAnalysis", JSON.stringify(data));
        navigate("/preview");
      } else {
        alert("Fehler beim Analysieren der Datei");
      }
    } catch (error) {
      console.error("Error:", error);
      alert("Fehler beim Hochladen");
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (file.name.toLowerCase().endsWith(".fit")) {
        setTestFile(file);
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link to="/" className="flex items-center gap-2">
              <Zap className="h-6 w-6 sm:h-8 sm:w-8 text-volt" />
              <span className="font-display font-bold text-lg sm:text-xl tracking-tight">VELOMETRICS</span>
              <span className="text-xs text-muted-foreground hidden md:inline">by Loops Cycling Academy</span>
            </Link>
            <div className="flex items-center gap-2 sm:gap-4">
              <LanguageSwitcher variant="minimal" />
              {user ? (
                <Link to="/dashboard">
                  <Button className="skew-btn bg-volt text-black hover:bg-volt/90 font-bold uppercase tracking-wider px-3 sm:px-6 text-xs sm:text-sm">
                    <span>{t('nav.dashboard')}</span>
                  </Button>
                </Link>
              ) : (
                <>
                  <Link to="/login" className="nav-link text-xs sm:text-sm font-medium hidden sm:block">
                    {t('nav.login')}
                  </Link>
                  <Link to="/register">
                    <Button className="skew-btn bg-volt text-black hover:bg-volt/90 font-bold uppercase tracking-wider px-3 sm:px-6 text-xs sm:text-sm">
                      <span className="hidden sm:inline">{t('landing.hero.cta')}</span>
                      <span className="sm:hidden">Start</span>
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 sm:pt-32 sm:pb-24 lg:pt-40 lg:pb-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1706974087241-d0fde894e93c?crop=entropy&cs=srgb&fm=jpg&q=85')" }}
        >
          <div className="absolute inset-0 hero-gradient"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7 animate-slide-up">
              <p className="data-label mb-2 sm:mb-4 text-xs sm:text-sm">{t('landing.hero.badge')}</p>
              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase mb-4 sm:mb-6">
                {t('landing.hero.title1')} <span className="text-volt">{t('landing.hero.title2')}</span>
              </h1>
              <p className="text-base sm:text-lg text-muted-foreground mb-6 sm:mb-8 max-w-xl">
                {t('landing.hero.subtitle')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Link to={user ? "/upload" : "/register"} data-testid="hero-cta-btn" className="w-full sm:w-auto">
                  <Button className="w-full sm:w-auto skew-btn bg-volt text-black hover:bg-volt/90 font-bold uppercase tracking-wider px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg">
                    <span>{t('landing.hero.cta')}</span>
                  </Button>
                </Link>
                <Link to="/pricing" data-testid="pricing-link" className="w-full sm:w-auto">
                  <Button variant="outline" className="w-full sm:w-auto border-white/20 hover:bg-white/10 text-white uppercase tracking-wider px-6 sm:px-8 py-4 sm:py-6">
                    {t('nav.pricing')}
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="lg:col-span-5 hidden lg:block">
              <div className="glass-card rounded-xl p-6 animate-slide-up" style={{ animationDelay: "0.2s" }}>
                <div className="flex items-center justify-between mb-4">
                  <span className="data-label">{t('analysis.title')}</span>
                  <div className="h-2 w-2 rounded-full bg-volt animate-pulse"></div>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-end">
                    <div>
                      <p className="data-label">{t('analysis.ftp')}</p>
                      <p className="number-display text-4xl text-volt">285<span className="text-lg ml-1">W</span></p>
                    </div>
                    <div className="text-right">
                      <p className="data-label">{t('analysis.vo2max')}</p>
                      <p className="number-display text-4xl text-cyan">58.2</p>
                    </div>
                  </div>
                  <div className="h-24 flex items-end gap-1">
                    {[40, 55, 65, 80, 95, 85, 70, 90, 100, 75, 60, 50].map((h, i) => (
                      <div 
                        key={i} 
                        className="flex-1 bg-volt/30 rounded-t"
                        style={{ height: `${h}%` }}
                      ></div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 sm:py-24 lg:py-32 grid-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-16">
            <p className="data-label mb-2 sm:mb-4 text-xs sm:text-sm">{t('landing.features.title')}</p>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight uppercase">
              {t('landing.features.title')}
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {[
              {
                icon: Activity,
                title: t('landing.features.ftp.title'),
                description: t('landing.features.ftp.desc')
              },
              {
                icon: TrendingUp,
                title: t('landing.features.vo2max.title'),
                description: t('landing.features.vo2max.desc')
              },
              {
                icon: Target,
                title: t('landing.features.zones.title'),
                description: t('landing.features.zones.desc')
              },
              {
                icon: Zap,
                title: t('landing.features.bio.title'),
                description: t('landing.features.bio.desc')
              },
              {
                icon: Users,
                title: t('landing.features.peer.title'),
                description: t('landing.features.peer.desc')
              },
              {
                icon: FileText,
                title: t('landing.features.pdf.title'),
                description: t('landing.features.pdf.desc')
              },
              {
                icon: Heart,
                title: t('landing.features.hr.title'),
                description: t('landing.features.hr.desc')
              },
              {
                icon: Flame,
                title: t('landing.features.energy.title'),
                description: t('landing.features.energy.desc'),
                highlight: true
              }
            ].map((feature, index) => (
              <div 
                key={index}
                className={`glass-card rounded-xl p-4 sm:p-6 stat-card ${feature.highlight ? 'border border-volt/30 bg-gradient-to-br from-volt/5 to-transparent' : ''}`}
                data-testid={`feature-card-${index}`}
              >
                <feature.icon className={`h-6 w-6 sm:h-10 sm:w-10 mb-2 sm:mb-4 ${feature.highlight ? 'text-cyan' : 'text-volt'}`} />
                <h3 className="font-display font-bold text-sm sm:text-lg mb-1 sm:mb-2 uppercase tracking-tight">{feature.title}</h3>
                <p className="text-muted-foreground text-xs sm:text-sm line-clamp-2 sm:line-clamp-none">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Test Upload Section */}
      <section className="py-16 sm:py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <p className="data-label mb-2 sm:mb-4 text-xs sm:text-sm">{t('landing.preview.title')}</p>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight uppercase">
              {t('landing.preview.title')}
            </h2>
            <p className="text-muted-foreground mt-3 sm:mt-4 max-w-2xl mx-auto text-sm sm:text-base px-4">
              {t('landing.preview.subtitle')}
            </p>
          </div>
          
          <div className="max-w-xl mx-auto">
            <div 
              className={`glass-card rounded-xl p-6 sm:p-8 border-2 border-dashed transition-colors cursor-pointer ${
                dragActive ? 'border-volt bg-volt/5' : 'border-white/20 hover:border-volt/50'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".fit"
                onChange={(e) => e.target.files[0] && setTestFile(e.target.files[0])}
                className="hidden"
              />
              
              {testFile ? (
                <div className="text-center">
                  <FileText className="h-12 w-12 text-volt mx-auto mb-4" />
                  <p className="font-medium text-lg mb-2">{testFile.name}</p>
                  <p className="text-sm text-muted-foreground mb-4">
                    {(testFile.size / 1024 / 1024).toFixed(2)} MB
                  </p>
                  
                  {/* Weight input for VO2max calculation */}
                  <div className="flex items-center justify-center gap-2 mb-6" onClick={(e) => e.stopPropagation()}>
                    <Scale className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Gewicht:</span>
                    <Input
                      type="number"
                      value={testWeight}
                      onChange={(e) => setTestWeight(e.target.value)}
                      className="w-20 h-8 bg-zinc-900 border-zinc-700 text-center"
                      placeholder="75"
                    />
                    <span className="text-sm text-muted-foreground">kg</span>
                  </div>
                  
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTestUpload();
                    }}
                    disabled={uploading}
                    className="skew-btn bg-volt text-black hover:bg-volt/90 font-bold uppercase tracking-wider px-8"
                  >
                    <span className="flex items-center gap-2">
                      {uploading ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Analysiere...
                        </>
                      ) : (
                        <>
                          <Activity className="h-4 w-4" />
                          Jetzt analysieren
                        </>
                      )}
                    </span>
                  </Button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setTestFile(null);
                    }}
                    className="block mx-auto mt-4 text-sm text-muted-foreground hover:text-white"
                  >
                    Andere Datei wählen
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <Upload className="h-12 w-12 text-volt/50 mx-auto mb-4" />
                  <p className="font-medium text-lg mb-2">FIT-Datei hierher ziehen</p>
                  <p className="text-sm text-muted-foreground mb-4">oder klicken zum Auswählen</p>
                  <p className="text-xs text-muted-foreground">
                    Unterstützt: .FIT-Dateien von Garmin, Wahoo, Zwift etc.
                  </p>
                </div>
              )}
            </div>
            
            <p className="text-center text-xs text-muted-foreground mt-4">
              Deine Datei wird nicht gespeichert und nach der Analyse gelöscht.
            </p>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="data-label mb-4">Preise</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight uppercase">
              Wähle deinen <span className="text-volt">Plan</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Single Analysis */}
            <div className="glass-card rounded-xl p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-volt/10 rounded-full blur-3xl"></div>
              <p className="data-label mb-2">Einzelanalyse</p>
              <div className="flex items-end gap-1 mb-2">
                <span className="number-display text-5xl text-volt">14,99</span>
                <span className="text-xl text-muted-foreground mb-2">€</span>
              </div>
              <p className="text-sm text-cyan font-medium mb-4">Optimal für Sportler</p>
              <p className="text-muted-foreground mb-4">Einmalige Zahlung für eine vollständige Analyse</p>
              <p className="text-sm text-volt/80 mb-6 border-l-2 border-volt/50 pl-3">
                Umfassendes biometrisches Profil zur optimalen Trainingssteuerung
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "1 komplette FTP-Analyse",
                  "Alle 7 Leistungszonen",
                  "VO2max Berechnung",
                  "VT1 & VT2 Schwellen",
                  "Kohlenhydratverbrauch",
                  "PDF Report zum Download",
                  "Peer-Group Vergleich"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <div className="h-1.5 w-1.5 rounded-full bg-volt"></div>
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/pricing" data-testid="single-pricing-btn">
                <Button variant="outline" className="w-full border-white/20 hover:bg-white/10 uppercase tracking-wider">
                  Auswählen
                </Button>
              </Link>
            </div>

            {/* Subscription */}
            <div className="glass-card rounded-xl p-8 relative overflow-hidden border border-volt/30">
              <div className="absolute top-0 right-0 w-32 h-32 bg-volt/20 rounded-full blur-3xl"></div>
              <div className="absolute top-4 right-4">
                <span className="bg-volt text-black text-xs font-bold px-3 py-1 rounded-full uppercase">Empfohlen</span>
              </div>
              <p className="data-label mb-2">Monatsabo</p>
              <div className="flex items-end gap-1 mb-2">
                <span className="number-display text-5xl text-volt">49,99</span>
                <span className="text-xl text-muted-foreground mb-2">€/Monat</span>
              </div>
              <p className="text-sm text-cyan font-medium mb-4">Für Coaches oder Professionals</p>
              <p className="text-muted-foreground mb-4">5 Analysen inklusive, jede weitere nur 9,99€</p>
              <p className="text-sm text-volt/80 mb-6 border-l-2 border-volt/50 pl-3">
                Für Coaches oder Professionals die regelmäßig auswerten - günstig, planbar, skalierbar
              </p>
              <ul className="space-y-3 mb-8">
                {[
                  "5 FTP-Analysen pro Monat inklusive",
                  "Weitere Analysen: je 9,99€",
                  "Alle 7 Leistungszonen",
                  "VO2max Berechnung",
                  "VT1 & VT2 Schwellen",
                  "Kohlenhydratverbrauch",
                  "PDF Reports zum Download",
                  "Peer-Group Vergleich",
                  "Monatlich kündbar"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <div className="h-1.5 w-1.5 rounded-full bg-volt"></div>
                    {item}
                  </li>
                ))}
              </ul>
              <Link to="/pricing" data-testid="subscription-pricing-btn">
                <Button className="w-full skew-btn bg-volt text-black hover:bg-volt/90 font-bold uppercase tracking-wider">
                  <span>Auswählen</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 lg:py-32 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight uppercase mb-6">
            Bereit für deine <span className="text-volt">Analyse</span>?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            {t('landing.cta.subtitle')}
          </p>
          <Link to={user ? "/upload" : "/register"} data-testid="cta-btn">
            <Button className="skew-btn bg-volt text-black hover:bg-volt/90 font-bold uppercase tracking-wider px-10 py-6 text-lg animate-pulse-glow">
              <span>{t('landing.cta.button')}</span>
            </Button>
          </Link>
        </div>
      </section>

      {/* Coaching CTA Section */}
      <section className="py-20 lg:py-28 border-t border-white/5 bg-gradient-to-b from-transparent to-cyan/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan/10 border border-cyan/30 mb-6">
            <TrendingUp className="h-4 w-4 text-cyan" />
            <span className="text-sm text-cyan font-medium">{t('landing.coaching.badge')}</span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight uppercase mb-6">
            {t('landing.coaching.title')} <span className="text-cyan">{t('landing.coaching.titleHighlight')}</span>
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            {t('landing.coaching.subtitle')}
          </p>
          <a 
            href="https://www.loops-cycling-academy.de/coaching-1/#Zu-den-Angeboten" 
            target="_blank" 
            rel="noopener noreferrer"
            data-testid="coaching-cta-btn"
          >
            <Button className="skew-btn bg-cyan text-black hover:bg-cyan/90 font-bold uppercase tracking-wider px-10 py-6 text-lg group">
              <span>{t('landing.coaching.cta')}</span>
              <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-6">
            {/* Top Row - Logo and Links */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-volt" />
                <span className="font-display font-bold text-sm">VELOMETRICS</span>
                <span className="text-xs text-muted-foreground">by Loops Cycling Academy</span>
              </div>
              
              {/* Legal Links */}
              <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
                <a 
                  href="https://www.loops-cycling-academy.de/impressum/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-white transition-colors"
                >
                  {t('footer.imprint')}
                </a>
                <span className="text-white/20">|</span>
                <a 
                  href="https://www.loops-cycling-academy.de/datenschutzerklarung/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-white transition-colors"
                >
                  {t('footer.privacy')}
                </a>
                <span className="text-white/20">|</span>
                <a 
                  href="https://www.loops-cycling-academy.de/agbs/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-white transition-colors"
                >
                  {t('footer.terms')}
                </a>
                <span className="text-white/20">|</span>
                <a 
                  href="https://www.loops-cycling-academy.de/widerrufsbelehrung/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-white transition-colors"
                >
                  {t('footer.cancellation')}
                </a>
              </div>
            </div>
            
            {/* Bottom Row - Copyright */}
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                © {new Date().getFullYear()} VeloMetrics by Loops Cycling Academy. {t('footer.rights')}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
